import React, { useEffect, useMemo, useState } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  LineChart,
  Line,
  ReferenceArea,
} from 'recharts';
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp } from 'lucide-react';
import {
  applyInfraFilters,
  calculateInfraAverageDurationHours,
  buildFilterLabel,
  distinctRowsBy,
  formatInfraDateTime,
  formatInfraDurationHours,
  formatInfraMonth,
  getInfraHistoryMonth,
  isInfraClosedInPeriod,
  normalizeDisplayValue,
  normalizeNumber,
  parseInfraDateTimeValue,
  sameFilter,
} from '../utils/infraAnalytics';
import { useInfraestruturaData } from '../context/InfraestruturaDataContext';
import InfraEvolutionNotice from './InfraEvolutionNotice';

const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const darkPanelClassName =
  'rounded-2xl border border-[color:var(--border)] bg-[color:var(--surface)] p-4 shadow-[0_14px_34px_rgba(15,23,42,0.08)]';

const chartTooltipStyle = {
  background: 'var(--surface-strong)',
  border: '1px solid var(--border)',
  borderRadius: '12px',
  color: 'var(--text-primary)',
  boxShadow: '0 18px 40px rgba(15,23,42,0.12)',
};

const TABLE_COLUMNS = [
  { key: 'funcionario', label: 'Técnico', type: 'text', minWidth: '11rem' },
  { key: 'cidade', label: 'Cidade', type: 'text', minWidth: '9rem' },
  { key: 'assunto', label: 'Assunto', type: 'text', minWidth: '14rem' },
  { key: 'estrutura', label: 'Estrutura', type: 'text', minWidth: '12rem' },
  { key: 'caixa', label: 'Caixa', type: 'text', minWidth: '9rem' },
  { key: 'projeto', label: 'Projeto', type: 'text', minWidth: '10rem' },
  { key: 'abertura', label: 'Abertura', type: 'date', minWidth: '9rem' },
  { key: 'execucao', label: 'Execução', type: 'date', minWidth: '9rem' },
  { key: 'fechamento', label: 'Fechamento', type: 'date', minWidth: '9rem' },
  { key: 'materiais', label: 'Materiais', type: 'number', align: 'right', minWidth: '6rem' },
  { key: 'valor', label: 'Valor', type: 'number', align: 'right', minWidth: '8rem' },
  { key: 'duracao', label: 'Execução → fechamento', type: 'number', align: 'right', minWidth: '11rem' },
];

const PAGE_SIZE_OPTIONS = [25, 50, 100];

const DEFAULT_SORT = { key: 'fechamento', direction: 'desc' };

const compareTableRows = (a, b, column, direction) => {
  const factor = direction === 'asc' ? 1 : -1;

  if (column.type === 'text') {
    return String(a[column.key]).localeCompare(String(b[column.key]), 'pt-BR') * factor;
  }

  const left = column.type === 'date' ? a[`${column.key}Value`] : a[column.key];
  const right = column.type === 'date' ? b[`${column.key}Value`] : b[column.key];

  // Linhas sem data ou sem medição ficam sempre no fim, independentemente da direção.
  if (left === null && right === null) return 0;
  if (left === null) return 1;
  if (right === null) return -1;

  return (left - right) * factor;
};

const SummaryCard = ({ title, value, hint }) => (
  <div className={`${darkPanelClassName} flex h-full min-h-[148px] flex-col`}>
    <p className="max-w-full text-[10px] font-semibold uppercase tracking-[0.10em] text-[color:var(--text-muted)] sm:text-[11px]">{title}</p>
    <p className="mt-3 break-words text-[clamp(1.9rem,2.4vw,3rem)] font-semibold leading-[0.94] tracking-[-0.03em] text-[color:var(--text-primary)]">{value}</p>
    {hint ? <p className="mt-auto pt-3 text-xs leading-5 text-[color:var(--text-muted)]">{hint}</p> : null}
  </div>
);

const SortIndicator = ({ active, direction }) => {
  if (!active) {
    return <ChevronDown className="h-3 w-3 shrink-0 opacity-0 transition-opacity group-hover:opacity-40" aria-hidden="true" />;
  }

  const Icon = direction === 'asc' ? ChevronUp : ChevronDown;

  return <Icon className="h-3 w-3 shrink-0 text-[color:var(--accent)]" aria-hidden="true" />;
};

const HistoricalDot = ({ cx, cy, payload, stroke, activeRangeMonth }) => {
  if (cx == null || cy == null) {
    return null;
  }

  const isActive = payload?.rawMonth === activeRangeMonth;

  if (!isActive) {
    return <circle cx={cx} cy={cy} r={3} fill={stroke} stroke="#ffffff" strokeWidth={1.5} />;
  }

  return (
    <g>
      <circle cx={cx} cy={cy} r={9} fill="rgba(96,165,250,0.18)" />
      <circle cx={cx} cy={cy} r={5} fill={stroke} stroke="#dbeafe" strokeWidth={2} />
    </g>
  );
};

const buildCollaboratorStats = (rows) => {
  const map = new Map();

  distinctRowsBy(rows, 'id_os').forEach((row) => {
    const name = normalizeDisplayValue(row?.funcionario);
    const bucket = map.get(name) || {
      name,
      os: new Set(),
      itens: 0,
      valor: 0,
      horasSum: 0,
      horasCount: 0,
      meses: new Map(),
    };

    const id = String(row?.id_os ?? '').trim();
    if (id) {
      bucket.os.add(id);
    }

    bucket.itens += normalizeNumber(row?.total_itens);
    bucket.valor += normalizeNumber(row?.total_valor);

    const horas = normalizeNumber(row?.diferenca_fechamento_horas);
    if (Number.isFinite(horas) && horas > 0) {
      bucket.horasSum += horas;
      bucket.horasCount += 1;
    }

    const month = getInfraHistoryMonth(row, 'ano_mes_fechamento');
    if (month !== 'Não informado') {
      bucket.meses.set(month, (bucket.meses.get(month) || 0) + 1);
    }

    map.set(name, bucket);
  });

  return Array.from(map.values())
    .map((bucket) => ({
      name: bucket.name,
      os: bucket.os.size,
      itens: bucket.itens,
      valor: bucket.valor,
      mediaHoras: bucket.horasCount > 0 ? bucket.horasSum / bucket.horasCount : 0,
      meses: bucket.meses,
    }))
    .sort((a, b) => b.os - a.os || a.name.localeCompare(b.name, 'pt-BR'));
};

const ColaboradorDashboard = ({ isMobile = false }) => {
  const {
    rowsRaw,
    rowsFiltered,
    evolutionMonths,
    filters,
    toggleFilter,
    selectHistoricalMonth,
    activeRangeMonth,
  } = useInfraestruturaData();
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [page, setPage] = useState(1);

  const closedRows = useMemo(() => rowsFiltered.filter((row) => isInfraClosedInPeriod(row)), [rowsFiltered]);
  const rowsWithoutCollaboratorFilter = useMemo(
    () => applyInfraFilters(rowsRaw, filters.filter((filter) => filter.field !== 'funcionario')),
    [filters, rowsRaw],
  );
  const closedRowsWithoutCollaboratorFilter = useMemo(
    () => rowsWithoutCollaboratorFilter.filter((row) => isInfraClosedInPeriod(row)),
    [rowsWithoutCollaboratorFilter],
  );
  const uniqueRows = useMemo(() => distinctRowsBy(closedRows, 'id_os'), [closedRows]);
  const colaboradorStatsWithoutCollaboratorFilter = useMemo(
    () => buildCollaboratorStats(closedRowsWithoutCollaboratorFilter),
    [closedRowsWithoutCollaboratorFilter],
  );

  const kpis = useMemo(() => {
    const totalOs = distinctRowsBy(closedRows, 'id_os').length;
    const totalItens = closedRows.reduce((acc, row) => acc + normalizeNumber(row?.total_itens), 0);
    const totalValor = closedRows.reduce((acc, row) => acc + normalizeNumber(row?.total_valor), 0);
    const mediaAberturaExecucao = calculateInfraAverageDurationHours(closedRows, 'data_abertura', 'data_hora_execucao');
    const mediaExecucaoFechamento = calculateInfraAverageDurationHours(closedRows, 'data_hora_execucao', 'data_fechamento');
    const mediaAberturaFechamento = calculateInfraAverageDurationHours(closedRows, 'data_abertura', 'data_fechamento');

    return {
      totalOs,
      totalItens,
      totalValor,
      mediaAberturaExecucao,
      mediaExecucaoFechamento,
      mediaAberturaFechamento,
    };
  }, [closedRows]);

  const rankingData = useMemo(
    () => colaboradorStatsWithoutCollaboratorFilter.slice(0, 10).map((item) => ({
      name: item.name,
      value: item.os,
    })),
    [colaboradorStatsWithoutCollaboratorFilter],
  );

  // Os filtros cruzados já foram aplicados em rowsFiltered pelo contexto; a tabela apenas
  // ordena e pagina esse recorte.
  const tableRows = useMemo(
    () => uniqueRows.map((row) => {
      const abertura = parseInfraDateTimeValue(row?.data_abertura);
      const execucao = parseInfraDateTimeValue(row?.data_hora_execucao);
      const fechamento = parseInfraDateTimeValue(row?.data_fechamento);
      const duracao = normalizeNumber(row?.diferenca_fechamento_horas);

      return {
        id: String(row?.id_os ?? ''),
        funcionario: normalizeDisplayValue(row?.funcionario),
        cidade: normalizeDisplayValue(row?.cidade_nome),
        assunto: normalizeDisplayValue(row?.assunto),
        estrutura: normalizeDisplayValue(row?.estrutura_descricao || row?.estrutura_referencia_normalizada),
        caixa: normalizeDisplayValue(row?.caixa_descricao),
        projeto: normalizeDisplayValue(row?.projeto_nome),
        abertura: formatInfraDateTime(row?.data_abertura),
        aberturaValue: abertura ? abertura.getTime() : null,
        execucao: formatInfraDateTime(row?.data_hora_execucao),
        execucaoValue: execucao ? execucao.getTime() : null,
        fechamento: formatInfraDateTime(row?.data_fechamento),
        fechamentoValue: fechamento ? fechamento.getTime() : null,
        materiais: normalizeNumber(row?.total_itens),
        valor: normalizeNumber(row?.total_valor),
        duracao: Number.isFinite(duracao) && duracao > 0 ? duracao : null,
      };
    }),
    [uniqueRows],
  );

  const sortedTableRows = useMemo(() => {
    const column = TABLE_COLUMNS.find((item) => item.key === sort.key) || TABLE_COLUMNS[0];

    return [...tableRows].sort((a, b) => (
      compareTableRows(a, b, column, sort.direction) || a.id.localeCompare(b.id, 'pt-BR')
    ));
  }, [sort.direction, sort.key, tableRows]);

  const totalPages = Math.max(1, Math.ceil(sortedTableRows.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageStartIndex = (currentPage - 1) * pageSize;

  const paginatedTableRows = useMemo(
    () => sortedTableRows.slice(pageStartIndex, pageStartIndex + pageSize),
    [pageSize, pageStartIndex, sortedTableRows],
  );

  // Um novo recorte de filtros ou de ordenação recomeça a leitura na primeira página.
  useEffect(() => {
    setPage(1);
  }, [filters, pageSize, sort]);

  const handleSort = (columnKey) => {
    setSort((current) => {
      if (current.key !== columnKey) {
        const column = TABLE_COLUMNS.find((item) => item.key === columnKey);
        return { key: columnKey, direction: column?.type === 'text' ? 'asc' : 'desc' };
      }

      return { key: columnKey, direction: current.direction === 'asc' ? 'desc' : 'asc' };
    });
  };

  const monthlyData = useMemo(() => {
    if (Array.isArray(evolutionMonths) && evolutionMonths.length > 0) {
      return evolutionMonths.map((item) => ({
        month: formatInfraMonth(item?.ano_mes || 'Não informado'),
        rawMonth: item?.ano_mes || 'Não informado',
        ordens: Number(item?.finalizadas ?? item?.total_os ?? 0),
        valor: Number(item?.total_valor ?? 0),
      }));
    }

    const months = new Map();

    uniqueRows.forEach((row) => {
      const month = getInfraHistoryMonth(row, 'ano_mes_fechamento');
      if (month === 'Não informado') return;

      if (!months.has(month)) {
        months.set(month, { month: formatInfraMonth(month), rawMonth: month, ordens: 0, valor: 0 });
      }

      const bucket = months.get(month);
      bucket.ordens += 1;
      bucket.valor += normalizeNumber(row?.total_valor);
    });

    return Array.from(months.values()).sort((a, b) => a.rawMonth.localeCompare(b.rawMonth, 'pt-BR'));
  }, [evolutionMonths, uniqueRows]);

  const handleCollaboratorClick = (name) => {
    toggleFilter({
      field: 'funcionario',
      value: name === 'Não informado' ? null : name,
      label: buildFilterLabel('funcionario', name === 'Não informado' ? null : name),
    });
  };

  const handleMonthSelect = (payload) => {
    const candidate = payload?.activePayload?.[0]?.payload
      || payload?.activePayload?.[1]?.payload
      || payload?.payload
      || payload;

    const rawMonth = candidate?.rawMonth || candidate?.month;

    if (rawMonth && rawMonth !== 'Não informado') {
      selectHistoricalMonth(rawMonth);
    }
  };

  const maxChartItems = Math.max(rankingData.length * 36, 280);
  const chartTickProps = isMobile ? { fontSize: 10 } : { fontSize: 11 };
  const chartMargin = isMobile
    ? { top: 8, right: 8, left: 0, bottom: 8 }
    : { top: 8, right: 20, left: 20, bottom: 8 };
  const historyMargin = isMobile
    ? { top: 8, right: 8, left: -14, bottom: 8 }
    : { top: 8, right: 20, left: 0, bottom: 8 };
  const historyXAxisProps = isMobile
    ? { interval: 'preserveStartEnd', minTickGap: 22, tickMargin: 8 }
    : { interval: 0, minTickGap: 10, tickMargin: 6 };
  const rankingAxisWidth = isMobile ? 96 : 180;
  const legendWrapperStyle = isMobile ? { paddingTop: 12, fontSize: 11 } : undefined;

  return (
    <div className="mt-4 w-full min-h-[720px] space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
        <SummaryCard title="OS finalizadas" value={kpis.totalOs.toLocaleString('pt-BR')} hint="Contagem distinta por id_os" />
        <SummaryCard title="Total de itens" value={kpis.totalItens.toLocaleString('pt-BR')} />
        <SummaryCard title="Total de valor" value={currencyFormatter.format(kpis.totalValor)} />
        <SummaryCard title="Abertura até execução" value={formatInfraDurationHours(kpis.mediaAberturaExecucao)} />
        <SummaryCard title="Execução até finalização" value={formatInfraDurationHours(kpis.mediaExecucaoFechamento)} />
        <SummaryCard title="Abertura até finalização" value={formatInfraDurationHours(kpis.mediaAberturaFechamento)} />
      </div>

      <div className="grid grid-cols-1 gap-3 xl:grid-cols-12">
        <div className={`${darkPanelClassName} xl:col-span-7`}>
          <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <h3 className="text-sm font-semibold text-[color:var(--text-primary)]">Ranking de colaboradores</h3>
            <span className="text-[11px] text-[color:var(--text-muted)]">Clique para cruzar filtros</span>
          </div>
          <div style={{ height: `${maxChartItems}px`, minHeight: '300px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={rankingData} layout="vertical" margin={chartMargin}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.20)" horizontal={false} />
                <XAxis type="number" allowDecimals={false} tick={chartTickProps} stroke="var(--text-muted)" />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={chartTickProps}
                  width={rankingAxisWidth}
                  stroke="var(--text-muted)"
                  tickFormatter={(value) => (String(value).length > (isMobile ? 16 : 26) ? `${String(value).slice(0, isMobile ? 16 : 26)}…` : value)}
                />
                <Tooltip contentStyle={chartTooltipStyle} labelStyle={{ color: 'var(--text-primary)' }} itemStyle={{ color: 'var(--text-primary)' }} />
                <Legend wrapperStyle={legendWrapperStyle} iconSize={isMobile ? 10 : 14} />
                <Bar
                  dataKey="value"
                  name="OS"
                  fill="#2563eb"
                  radius={[0, 8, 8, 0]}
                  onClick={(payload) => {
                    const item = payload?.payload || payload;
                    if (item?.name) {
                      handleCollaboratorClick(item.name);
                    }
                  }}
                >
                  {rankingData.map((entry) => (
                    <Cell key={entry.name} fill="#2563eb" />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className={`${darkPanelClassName} xl:col-span-5`}>
          <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-sm font-semibold text-[color:var(--text-primary)]">Evolução mensal</h3>
              {activeRangeMonth ? (
                <span className="rounded-full border border-[color:var(--accent)]/35 bg-[color:var(--accent)]/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-[color:var(--accent)]">
                  Mês ativo: {formatInfraMonth(activeRangeMonth)}
                </span>
              ) : null}
            </div>
          </div>
          <InfraEvolutionNotice />
          <div className="h-[340px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={monthlyData} margin={historyMargin} onClick={handleMonthSelect}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.20)" />
                {activeRangeMonth ? (
                  <ReferenceArea
                    x1={formatInfraMonth(activeRangeMonth)}
                    x2={formatInfraMonth(activeRangeMonth)}
                    fill="rgba(59,130,246,0.10)"
                    strokeOpacity={0}
                  />
                ) : null}
                <XAxis dataKey="month" tick={chartTickProps} stroke="var(--text-muted)" {...historyXAxisProps} />
                <YAxis yAxisId="left" tick={chartTickProps} width={isMobile ? 30 : 40} stroke="var(--text-muted)" />
                <YAxis yAxisId="right" orientation="right" tick={chartTickProps} width={isMobile ? 34 : 48} stroke="var(--text-muted)" />
                <Tooltip contentStyle={chartTooltipStyle} labelStyle={{ color: 'var(--text-primary)' }} itemStyle={{ color: 'var(--text-primary)' }} />
                <Legend wrapperStyle={legendWrapperStyle} iconSize={isMobile ? 10 : 14} />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="ordens"
                  stroke="#2563eb"
                  strokeWidth={2}
                  dot={(props) => <HistoricalDot {...props} activeRangeMonth={activeRangeMonth} />}
                  activeDot={{ r: 5 }}
                  name="OS"
                  onClick={(payload) => {
                    const item = payload?.payload || payload;
                    if (item?.rawMonth || item?.month) {
                      const rawMonth = item?.rawMonth || item?.month;
                      if (rawMonth && rawMonth !== 'Não informado') {
                        selectHistoricalMonth(rawMonth);
                      }
                    }
                  }}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="valor"
                  stroke="#0ea5e9"
                  strokeWidth={2}
                  dot={(props) => <HistoricalDot {...props} activeRangeMonth={activeRangeMonth} />}
                  activeDot={{ r: 5 }}
                  name="Valor"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3">
        <div className={darkPanelClassName}>
          <div className="mb-3 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <h3 className="text-sm font-semibold text-[color:var(--text-primary)]">Detalhamento por colaborador</h3>
              <p className="mt-1 text-xs text-[color:var(--text-muted)]">
                OS finalizadas no período, já com os filtros cruzados aplicados. Clique no cabeçalho para ordenar
                e no técnico para cruzar o filtro.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full border border-[color:var(--header-control-border-subtle)] px-2.5 py-1 text-[11px] text-[color:var(--text-muted)]">
                {`${sortedTableRows.length.toLocaleString('pt-BR')} OS`}
              </span>
              <label className="text-[11px] font-medium text-[color:var(--text-muted)]" htmlFor="colaborador-table-page-size">
                Linhas por página
              </label>
              <select
                id="colaborador-table-page-size"
                value={pageSize}
                onChange={(event) => setPageSize(Number(event.target.value))}
                className="rounded-full border border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface-strong)] px-3 py-1.5 text-[11px] font-medium text-[color:var(--text-primary)] outline-none transition-colors hover:border-[color:var(--accent)]/45 focus:border-[color:var(--accent)]/55"
              >
                {PAGE_SIZE_OPTIONS.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
          </div>

          <div
            data-testid="colaborador-table-scroll"
            className="overflow-x-auto rounded-xl border border-[color:var(--border)]/60"
          >
            <table className="min-w-[1180px] w-full text-xs">
              <caption className="sr-only">
                Ordens de serviço finalizadas por colaborador no período selecionado
              </caption>
              <thead className="bg-[color:var(--surface-strong)]">
                <tr className="border-b border-[color:var(--border)] text-left text-[color:var(--text-muted)]">
                  {TABLE_COLUMNS.map((column) => {
                    const isActive = sort.key === column.key;

                    return (
                      <th
                        key={column.key}
                        scope="col"
                        style={{ minWidth: column.minWidth }}
                        aria-sort={isActive ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
                        className="px-2 py-2 font-semibold uppercase tracking-[0.06em]"
                      >
                        <button
                          type="button"
                          onClick={() => handleSort(column.key)}
                          className={`group flex w-full items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.06em] transition-colors hover:text-[color:var(--text-primary)] ${
                            column.align === 'right' ? 'justify-end' : 'justify-start'
                          } ${isActive ? 'text-[color:var(--text-primary)]' : ''}`}
                        >
                          <span>{column.label}</span>
                          <SortIndicator active={isActive} direction={sort.direction} />
                        </button>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {paginatedTableRows.map((row) => {
                  const isCollaboratorActive = Array.isArray(filters)
                    && filters.some((filter) => sameFilter(filter, {
                      field: 'funcionario',
                      value: row.funcionario,
                    }));

                  return (
                    <tr
                      key={row.id}
                      className="border-b border-[color:var(--border)]/40 hover:bg-[color:var(--surface-muted)]/40"
                    >
                      <td className="px-2 py-2">
                        <button
                          type="button"
                          onClick={() => handleCollaboratorClick(row.funcionario)}
                          className={`max-w-full truncate rounded-full px-2 py-1 text-left text-xs font-medium transition-colors ${
                            isCollaboratorActive
                              ? 'bg-[color:var(--accent)]/12 text-[color:var(--accent)]'
                              : 'text-[color:var(--text-primary)] hover:bg-[color:var(--surface-muted)]/70'
                          }`}
                        >
                          {row.funcionario}
                        </button>
                      </td>
                      <td className="px-2 py-2">{row.cidade}</td>
                      <td className="px-2 py-2">{row.assunto}</td>
                      <td className="px-2 py-2">{row.estrutura}</td>
                      <td className="px-2 py-2">{row.caixa}</td>
                      <td className="px-2 py-2">{row.projeto}</td>
                      <td className="px-2 py-2">{row.abertura}</td>
                      <td className="px-2 py-2">{row.execucao}</td>
                      <td className="px-2 py-2">{row.fechamento}</td>
                      <td className="px-2 py-2 text-right">{row.materiais.toLocaleString('pt-BR')}</td>
                      <td className="px-2 py-2 text-right">{currencyFormatter.format(row.valor)}</td>
                      <td className="px-2 py-2 text-right">
                        {row.duracao === null ? 'Não informado' : formatInfraDurationHours(row.duracao)}
                      </td>
                    </tr>
                  );
                })}
                {paginatedTableRows.length === 0 ? (
                  <tr>
                    <td colSpan={TABLE_COLUMNS.length} className="px-2 py-8 text-center text-sm text-[color:var(--text-muted)]">
                      Nenhuma OS finalizada para os filtros e o período selecionados.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>

          <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-[11px] text-[color:var(--text-muted)]">
              {sortedTableRows.length === 0
                ? 'Nenhum registro para exibir'
                : `Exibindo ${(pageStartIndex + 1).toLocaleString('pt-BR')}–${Math.min(pageStartIndex + pageSize, sortedTableRows.length).toLocaleString('pt-BR')} de ${sortedTableRows.length.toLocaleString('pt-BR')}`}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage(Math.max(1, currentPage - 1))}
                disabled={currentPage <= 1}
                aria-label="Página anterior"
                className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface-strong)] text-[color:var(--text-primary)] transition-colors hover:border-[color:var(--accent)]/45 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="text-[11px] font-medium text-[color:var(--text-muted)]">
                {`Página ${currentPage.toLocaleString('pt-BR')} de ${totalPages.toLocaleString('pt-BR')}`}
              </span>
              <button
                type="button"
                onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage >= totalPages}
                aria-label="Próxima página"
                className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface-strong)] text-[color:var(--text-primary)] transition-colors hover:border-[color:var(--accent)]/45 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ColaboradorDashboard;
